import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useLoginWithGoogleMutation,
  useGenerateEmailOtpMutation,
  useSendPhoneOtpMutation,
  useVerifyEmailOtpMutation,
  useVerifyPhoneOtpMutation,
} from "../../../utils/apiSlice";
import "./register.scss";
import { triggerCoins } from "../../canvasConfict";
import { Mail, Smartphone } from "lucide-react";
import GoogleIconSignIn from "../../../components/auth/GoogleIconSignIn";
import AuthIconTooltip from "../../../components/auth/AuthIconTooltip";
import ContactEntryDialogs from "../../../components/auth/ContactEntryDialogs";
import {
  isValidContactEmail,
  isValidContactPhoneDigits,
  type ContactEntryFieldErrors,
  type SeedRegistrationOtpState,
} from "../../../utils/contactEntryValidation";
import { loginSuccess } from "../../../utils/authSlice";
import { runLoginGamification } from "../../../utils/gamificationService";
import { hydrateGamification } from "../../../utils/gamificationSlice";
import { setPendingSocialPrefill, clearPendingSocialPrefill } from "../../../utils/socialPrefill";
import { markGoogleAssistRegisterPending } from "../../../utils/gamificationService";

const Register = () => {
  const [country, setCountry] = useState({
    code: "+91",
    flag: "🇮🇳",
    maxLength: 10,
  });
  const [loginWithGoogle] = useLoginWithGoogleMutation();
  const dispatch = useDispatch();
  const [isExistingUser, setIsExistingUser] = useState(true)
  const navigate = useNavigate();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState("email"); // Default to email
  const [registerData, setRegisterData] = useState<any>(null);
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [hasOtpError, setHasOtpError] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [generateEmailOtpMutation] = useGenerateEmailOtpMutation();
  const [verifyEmailOtpMutation] = useVerifyEmailOtpMutation();
  const [sendPhoneOtpMutation] = useSendPhoneOtpMutation();
  const [verifyPhoneOtpMutation] = useVerifyPhoneOtpMutation();
  const inputRefs = React.useRef<Array<HTMLInputElement | null>>([]);
  const seedOtpHandledRef = React.useRef(false);

  const [contactEntryKind, setContactEntryKind] = useState<null | "email" | "phone">(null);
  const [draftFullName, setDraftFullName] = useState("");
  const [draftEmail, setDraftEmail] = useState("");
  const [draftPhone, setDraftPhone] = useState("");
  const [contactFieldErrors, setContactFieldErrors] = useState<ContactEntryFieldErrors>({});

  const clearContactFieldError = (field: "name" | "email" | "phone") => {
    setContactFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };
  const [googleLoading, setGoogleLoading] = useState(false);
  const [isConsent, setIsConsent] = useState(false);

  const persistSessionFromPayload = (payloadData: any, storage: Storage) => {
    const tokenString =
      payloadData?.token?.accessToken ||
      payloadData?.token ||
      payloadData?.data?.token?.accessToken ||
      payloadData?.data?.token;
    const patientProfile =
      payloadData?.patientProfile ||
      payloadData?.data?.patientProfile ||
      payloadData?.user ||
      payloadData?.data?.user ||
      {};
    if (!tokenString) return;

    const normalizedPatientId =
      patientProfile?.patientId ??
      patientProfile?.patientID ??
      patientProfile?.patient_id ??
      payloadData?.patientId ??
      payloadData?.patientID ??
      payloadData?.patient_id ??
      payloadData?.data?.patientId ??
      payloadData?.data?.patientID ??
      payloadData?.data?.patient_id ??
      payloadData?.patientProfileId ??
      payloadData?.data?.patientProfileId ??
      null;

    const mergedProfile =
      normalizedPatientId != null && normalizedPatientId !== ""
        ? { ...patientProfile, patientId: normalizedPatientId }
        : patientProfile;
    const userId =
      mergedProfile?.userId ??
      mergedProfile?.id ??
      payloadData?.userId ??
      payloadData?.id ??
      null;
    const safeRoles = mergedProfile?.role || payloadData?.roles || "USER";
    const familyMembers = payloadData?.familyMembers || [];
    storage.setItem("token", tokenString);
    storage.setItem("userData", JSON.stringify(mergedProfile));
    storage.setItem("familyMembers", JSON.stringify(familyMembers));
    const rolesArray =
      typeof safeRoles === "string" && safeRoles.includes(",") ? safeRoles.split(",") : [safeRoles];
    storage.setItem("roles", JSON.stringify(rolesArray));
    dispatch(loginSuccess(tokenString));

    if (userId) {
      runLoginGamification(String(userId));
      dispatch(hydrateGamification(String(userId)));
    }
  };

  const closeContactEntry = () => {
    setIsExistingUser(true)
    setContactEntryKind(null);
    setContactFieldErrors({});
  };

  const assertConsent = (): boolean => {
    if (!isConsent) {
      toast.error("Please acknowledge the consent statement to proceed.");
      return false;
    }
    return true;
  };

  const openEmailEntry = () => {
    setContactFieldErrors({});
    setDraftFullName("");
    setDraftEmail("");
    setContactEntryKind("email");
  };

  const openPhoneEntry = () => {
    setContactFieldErrors({});
    setDraftFullName("");
    setDraftPhone("");
    setContactEntryKind("phone");
  };

  const submitEmailEntry = () => {
    const fullName = String(draftFullName || "").trim();
    const e = String(draftEmail).trim();
    const next: ContactEntryFieldErrors = {};
    if (!fullName && !isExistingUser) next.name = "Enter your full name.";
    if (!isValidContactEmail(e)) next.email = "Enter a valid email address.";
    if (Object.keys(next).length) {
      setContactFieldErrors(next);
      return;
    }
    setContactFieldErrors({});
    setRegisterData({ email: e, phoneNumber: "", name: fullName });
    setVerificationMethod("email");
    setContactEntryKind(null);
    sendNewOtp(e);
    setIsExistingUser(true)
  };

  const submitPhoneEntry = () => {
    const fullName = String(draftFullName || "").trim();
    const digits = draftPhone.replace(/\D/g, "").slice(0, 10);
    const next: ContactEntryFieldErrors = {};
    if (!fullName && !isExistingUser) next.name = "Enter your full name.";
    if (!isValidContactPhoneDigits(digits)) {
      next.phone = "Enter a valid 10-digit mobile number";
    }
    if (Object.keys(next).length) {
      setContactFieldErrors(next);
      return;
    }
    setContactFieldErrors({});
    setRegisterData({ email: "", phoneNumber: digits, name: fullName });
    setVerificationMethod("mobile");
    setContactEntryKind(null);
    sendNewOtp(digits, "mobile");
    setIsExistingUser(true)
  };

  const applyGooglePrefillForSignup = (credential: string) => {
    setPendingSocialPrefill({ idToken: credential, provider: "google" });
    markGoogleAssistRegisterPending();
    toast.success("Continue with Email or Phone to complete signup.");
  };

  const handleGoogleCredential = async (credential: string) => {
    if (!assertConsent()) return;
    setGoogleLoading(true);
    try {
      const response: any = await loginWithGoogle({ idToken: credential }).unwrap();
      const payloadData = response?.data ?? response;

      if (response?.data?.coinSummary?.coinsEarned) {
        triggerCoins(response?.data?.coinSummary?.coinsEarned ? response?.data?.coinSummary?.coinsEarned : 0, response?.data?.coinSummary?.totalCoins ? response?.data?.coinSummary?.totalCoins : 0)

      }
      if (response?.success || payloadData?.token || payloadData?.patientProfile) {
        persistSessionFromPayload(payloadData, localStorage);
        clearPendingSocialPrefill();
        toast.success(response?.message || "Login successful");
        navigate("/dashboard");
        return;
      }
    } catch (error: any) {
      if (error?.originalStatus === 200 || error?.status === 200) {
        const payloadData = error?.data?.data || error?.data || {};
        if (payloadData?.token?.accessToken || payloadData?.token || payloadData?.patientProfile) {
          persistSessionFromPayload(payloadData, localStorage);
          clearPendingSocialPrefill();
          toast.success(error?.data?.message || "Login successful");
          navigate("/dashboard");
          return;
        }
      }
    } finally {
      setGoogleLoading(false);
    }

    applyGooglePrefillForSignup(credential);
  };

  useEffect(() => {
    const st = location.state as { openContactEntry?: "email" | "phone" } | null | undefined;
    const which = st?.openContactEntry;
    if (which !== "email" && which !== "phone") return;
    if (!isConsent) return;
    setContactFieldErrors({});
    setDraftEmail("");
    setDraftPhone("");
    setContactEntryKind(which);
    navigate("/register", { replace: true, state: {} });
  }, [location.state, navigate, isConsent]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  // If verify-otp navigates here due to "account already exists", show toast here only.
  useEffect(() => {
    const pending = sessionStorage.getItem("pendingRegisterToast");
    if (pending) {
      // Ensure we land on plain Register screen (no OTP dialog) when redirecting here
      // from `/verify-otp` due to "account already exists".
      setShowModal(false);
      setOtpStep(false);
      setOtp(["", "", "", "", "", ""]);
      setHasOtpError(false);
      setCountdown(0);

      toast.error(pending);
      sessionStorage.removeItem("pendingRegisterToast");
    }
  }, []);

  const handleChangeOtp = (value: string, index: number) => {
    if (isNaN(Number(value))) return;

    setHasOtpError(false);

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);

    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    const otpValue = newOtp.join("");

    if (otpValue.length === 6 && !newOtp.includes("")) {
      handleVerifyOtp(otpValue);
    }
  };




  const handleKeyDownOtp = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      setHasOtpError(false);
      inputRefs.current[index - 1]?.focus();
    }
  };

  const getOtpCountdownSeconds = (resp: any, fallbackSeconds = 30) => {
    const rawSeconds =
      resp?.expiresInSeconds ??
      resp?.data?.expiresInSeconds ??
      resp?.expires_in_seconds ??
      resp?.data?.expires_in_seconds;

    const rawMinutes =
      resp?.expiresInMinutes ??
      resp?.data?.expiresInMinutes ??
      resp?.expires_in_minutes ??
      resp?.data?.expires_in_minutes;

    const seconds =
      typeof rawSeconds === "string" ? parseInt(rawSeconds, 10) : rawSeconds;
    if (typeof seconds === "number" && seconds > 0) return Math.min(seconds, fallbackSeconds);

    const minutes =
      typeof rawMinutes === "string" ? parseFloat(rawMinutes) : rawMinutes;
    if (typeof minutes === "number" && minutes > 0) return Math.min(Math.floor(minutes * 60), fallbackSeconds);

    return fallbackSeconds;
  };

  const sendNewOtp = async (contactIdentifier?: string | React.MouseEvent, method: "email" | "mobile" = verificationMethod as any, flag?: boolean | undefined) => {
    if (!isConsent) {
      toast.error("Please acknowledge the consent statement to proceed.");
      return;
    }
    const isEmail = method === "email";
    const defaultContact = isEmail ? registerData?.email : registerData?.phoneNumber;
    const contact = (typeof contactIdentifier === "string" ? contactIdentifier : undefined) || defaultContact;
    const e164Phone = !isEmail
      ? (() => {
        const digits = String(contact ?? "").replace(/\D/g, "").slice(0, 10);
        return digits ? `${country.code}${digits}` : "";
      })()
      : "";

    // Keep popup stable during "Resend" (when it's already open),
    // but for the initial "Verify" click (when it's closed) hide it until success.
    if (!showModal) {
      setShowModal(false);
      setOtpStep(false);
    }
    setHasOtpError(false);
    setCountdown(0);

    try {
      let response: any;
      if (isEmail) {
        response = await generateEmailOtpMutation({ email: contact }).unwrap();
      } else {

        const obj: any = {
          otpPurpose: 'REGISTRATION',
          phoneNumber: e164Phone
        }
        if (!flag) {
          delete obj.otpPurpose
        }
        response = await sendPhoneOtpMutation(obj).unwrap();
      }
      console.log(response)
      if (response?.success || response) {
        setShowModal(true);
        setCountdown(getOtpCountdownSeconds(response, 30));
        setOtpStep(true);
        setOtp(["", "", "", "", "", ""]);
        setHasOtpError(false);
        toast.info(response?.message || `OTP sent to your ${isEmail ? 'Email' : 'Mobile Number'}`);
      }
    } catch (error: any) {






      if (error?.status === 500 && error?.data == "Failed to send OTP via SMS: Authenticate") {

        return;
      }

      if (error?.status === 500) {
        setShowModal(true);
        setCountdown(getOtpCountdownSeconds(error, 30));
        setOtpStep(true);
        setOtp(["", "", "", "", "", ""]);
        setHasOtpError(false);
      }
      if (error?.originalStatus === 200 || error?.status === 200 || error?.status === 201) {
        setShowModal(true);
        setCountdown(getOtpCountdownSeconds(error?.data, 30));
        setOtpStep(true);
        setOtp(["", "", "", "", "", ""]);
        setHasOtpError(false);
        toast.info(error?.data?.message || `OTP sent to your ${method === "email" ? 'Email' : 'Mobile Number'}`);
        return;
      }

      let errorMsg = "Failed to send OTP.";
      if (typeof error?.data === "string") {
        errorMsg = error.data;
      } else if (error?.data?.message) {
        errorMsg = error.data.message;
      } else if (error?.data?.detail) {
        errorMsg = error.data.detail;
      } else if (typeof error?.error === "string") {
        // Check if RTK Query threw a PARSING_ERROR but captured the raw text
        if (error.error.includes("Unexpected token") && typeof error.data === "string") {
          errorMsg = error.data;
        } else if (error.error === "TypeError: Failed to fetch") {
          errorMsg = "Network Error: Failed to connect to the server. Please check your internet connection or try again later.";
        } else {
          errorMsg = error.error;
        }
      }

      if (
        typeof errorMsg === "string" &&
        errorMsg.toLowerCase().includes("email already verified") &&
        errorMsg.toLowerCase().includes("please proceed with signup")
      ) {
        // Keep as popup flow: open OTP step without changing URL.
        setShowModal(true);
        setOtpStep(true);
        setOtp(["", "", "", "", "", ""]);
        setCountdown(30);
        setHasOtpError(false);
        return;
      }

      // If backend says account already exists, don't keep user on OTP screen.
      if (
        typeof errorMsg === "string" &&
        (errorMsg.toLowerCase().includes("account with this email already exists") ||
          errorMsg.toLowerCase().includes("account with this phone number already exists") ||
          errorMsg.toLowerCase().includes("account with this mobile already exists") ||
          errorMsg.toLowerCase().includes("account already exists"))
      ) {
        setShowModal(false);
        setOtpStep(false);
        setOtp(["", "", "", "", "", ""]);
        setHasOtpError(false);
        setCountdown(0);
        toast.error(errorMsg);
        return;
      }

      if (
        typeof errorMsg === "string" &&
        errorMsg.toLowerCase().includes("otp already verified") &&
        (errorMsg.toLowerCase().includes("signup") ||
          errorMsg.toLowerCase().includes("proceed") ||
          errorMsg.toLowerCase().includes("register"))
      ) {
        // OTP is already verified on backend side; open OTP step so user can continue.
        setShowModal(true);
        setOtpStep(true);
        setOtp(["", "", "", "", "", ""]);
        setCountdown(30);
        setHasOtpError(false);
        return;
      }

      // Backend may respond with "OTP already sent..." - in this case we should
      // still open the OTP verify UI so the user can enter the existing OTP.
      if (
        typeof errorMsg === "string" &&
        errorMsg.toLowerCase().includes("otp already sent")
      ) {
        setShowModal(true);
        setOtpStep(true);
        setOtp(["", "", "", "", "", ""]);
        setCountdown(30);
        setHasOtpError(false);
        return;
      }

      // toast.error(errorMsg);
    }
  };

  const handleVerifyOtp = async (otpValue: any) => {
    if (!assertConsent()) return;
    try {
      const isEmail = verificationMethod === "email";
      const contact = isEmail ? registerData?.email : registerData?.phoneNumber;

      const resolvedName = String(registerData?.name || "").trim();

      let response: any;
      if (isEmail) {
        // if (!resolvedName) {
        //   toast.error("Please enter your full name first.");
        //   return;
        // }
        response = await verifyEmailOtpMutation({
          email: String(contact || "").trim(),
          otp: otpValue,
          username: resolvedName,
        }).unwrap();
      } else {
        response = await verifyPhoneOtpMutation({
          phoneNumber: `${country.code}${String(contact ?? "").replace(/\D/g, "").slice(0, 10)}`,
          otp: otpValue,
          name: resolvedName,
        }).unwrap();
      }

      toast.success(response?.message || "OTP Verified successfully!");
      setShowModal(false);


      if (response?.data?.coinSummary?.coinsEarned) {
        triggerCoins(response?.data?.coinSummary?.coinsEarned ? response?.data?.coinSummary?.coinsEarned : 0, response?.data?.coinSummary?.totalCoins ? response?.data?.coinSummary?.totalCoins : 0)

      }

      // If API returns auth session on verify, auto-login and go to dashboard
      const payloadData = response?.data ?? response;
      const tokenString = payloadData?.token?.accessToken || payloadData?.token;
      if (tokenString) {
        const patientProfile = payloadData?.patientProfile || {};
        const safeRoles = patientProfile?.role || payloadData?.roles || "USER";
        const familyMembers = payloadData?.familyMembers || [];
        localStorage.setItem("token", tokenString);
        localStorage.setItem("userData", JSON.stringify(patientProfile));
        localStorage.setItem("familyMembers", JSON.stringify(familyMembers));
        const rolesArray = typeof safeRoles === "string" && safeRoles.includes(",") ? safeRoles.split(",") : [safeRoles];
        localStorage.setItem("roles", JSON.stringify(rolesArray));
        dispatch(loginSuccess(tokenString));
        navigate("/dashboard");
      } else if (isEmail) {
        // Email verify completed — proceed to dashboard as requested.
        navigate("/dashboard");
      }
    } catch (error: any) {
      console.log(error)
      if (error?.originalStatus === 200 || error?.status === 200 || error?.status === 202) {
        toast.success(error?.data?.message || "OTP Verified successfully!");
        setShowModal(false);

        const payloadData = error?.data?.data ?? error?.data ?? {};
        const tokenString = payloadData?.token?.accessToken || payloadData?.token;
        if (tokenString) {
          const patientProfile = payloadData?.patientProfile || {};
          const safeRoles = patientProfile?.role || payloadData?.roles || "USER";
          const familyMembers = payloadData?.familyMembers || [];
          localStorage.setItem("token", tokenString);
          localStorage.setItem("userData", JSON.stringify(patientProfile));
          localStorage.setItem("familyMembers", JSON.stringify(familyMembers));
          const rolesArray = typeof safeRoles === "string" && safeRoles.includes(",") ? safeRoles.split(",") : [safeRoles];
          localStorage.setItem("roles", JSON.stringify(rolesArray));
          dispatch(loginSuccess(tokenString));
          navigate("/dashboard");
        } else if (verificationMethod === "email") {
          navigate("/dashboard");
        }
      } else {
        let errorMsg = "Invalid OTP. Please try again.";
        if (error.status == 500) {
          setHasOtpError(true);
          return

        }
        if (typeof error?.data === "string") {
          errorMsg = error.data;
        } else if (error?.data?.message) {
          errorMsg = error.data.message;
        } else if (error?.data?.detail) {
          errorMsg = error.data.detail;
        } else if (typeof error?.error === "string") {
          if (error.error.includes("Unexpected token") && typeof error.data === "string") {
            errorMsg = error.data;
          } else if (error.error === "TypeError: Failed to fetch") {
            errorMsg = "Network Error: Failed to connect to the server. Please check your internet connection or try again later.";
          } else {
            errorMsg = error.error;
          }
        }

        // If backend says account already exists, don't stay on OTP screen.
        if (
          typeof errorMsg === "string" &&
          (errorMsg.toLowerCase().includes("account with this email already exists") ||
            errorMsg.toLowerCase().includes("account with this phone number already exists") ||
            errorMsg.toLowerCase().includes("account with this mobile already exists") ||
            errorMsg.toLowerCase().includes("account already exists"))
        ) {
          setShowModal(false);
          setOtpStep(false);
          setOtp(["", "", "", "", "", ""]);
          setHasOtpError(false);
          setCountdown(0);
          toast.error(errorMsg);
          return;
        }

        setHasOtpError(true);
        toast.error(errorMsg);
      }
    }
  };

  // clear OTP inputs whenever modal opens so boxes are blank each time
  useEffect(() => {
    if (showModal) {
      setOtp(["", "", "", "", "", ""]);
      setHasOtpError(false);
      setCountdown(0);
      // do not reset otpStep here because caller (sendNewOtp)
      // may immediately set it to true after opening
    }
  }, [showModal]);

  // clear OTP inputs when user toggles between email/mobile while the modal is open
  useEffect(() => {
    if (showModal) {
      setOtp(["", "", "", "", "", ""]);
      setHasOtpError(false);
    }
  }, [verificationMethod, showModal]);

  /** Same as login “Next” on register page: start signup OTP here (already on /register). */
  useEffect(() => {
    const st = location.state as {
      seedRegistrationOtp?: SeedRegistrationOtpState;
      guestChatSignup?: boolean;
    } | null | undefined;
    const seed = st?.seedRegistrationOtp;
    if (!seed) {
      seedOtpHandledRef.current = false;
      return;
    }
    if (seedOtpHandledRef.current) return;
    if (!isConsent) return;

    const guestPreserve = st?.guestChatSignup ? ({ guestChatSignup: true } as const) : {};

    const isMobile = seed.method === "mobile";
    const contact = isMobile
      ? String(seed.phoneNumber ?? "").replace(/\D/g, "").slice(0, 10)
      : String(seed.email ?? "").trim();

    if (isMobile) {
      if (!isValidContactPhoneDigits(contact)) {
        toast.error("Enter a valid 10-digit mobile number");
        navigate("/register", { replace: true, state: guestPreserve });
        return;
      }
      seedOtpHandledRef.current = true;
      navigate("/register", { replace: true, state: guestPreserve });
      setRegisterData({ email: "", phoneNumber: contact, name: String(seed.name ?? "").trim() });
      setVerificationMethod("mobile");
      void sendNewOtp(contact, "mobile");
      return;
    }

    if (!isValidContactEmail(contact)) {
      toast.error("Enter a valid email address.");
      navigate("/register", { replace: true, state: guestPreserve });
      return;
    }
    seedOtpHandledRef.current = true;
    navigate("/register", { replace: true, state: guestPreserve });
    setRegisterData({
      email: contact,
      phoneNumber: "",
      name: String(seed.name ?? "").trim(),
    });
    setVerificationMethod("email");
    void sendNewOtp(contact, "email");
  }, [location.state, navigate, isConsent]);

  const guestChatSignup = Boolean(
    (location.state as { guestChatSignup?: boolean } | null | undefined)?.guestChatSignup
  );

  return (
    <div
      className={`container-fluid h-p100 auth-container${guestChatSignup ? " auth-container--guest-chat" : ""}`}
    >
      <div className="row align-items-center justify-content-md-center h-p100">
        <div className="col-12">
          <div className="row justify-content-center g-0">
            <div className="col-lg-5 col-md-5 col-12 wdt">
              <div className="rounded10 shadow-lg translucent-card">
                <div className="content-top-agile p-20 pb-0 text-center">
                  <h2>Register a New Membership</h2>
                </div>
                <div className="checkbox mb-3 px-40 mt-3">
                  <input
                    style={{ cursor: "pointer" }}
                    type="checkbox"
                    id="register-disclaimer-consent"
                    checked={isConsent}
                    onChange={(e) => setIsConsent(e.target.checked)}
                  />
                  <label htmlFor="register-disclaimer-consent" className="text-muted" style={{ cursor: "pointer" }}>
                    This tool is for educational purposes only. Our AI is not a doctor and cannot provide a medical
                    diagnosis or treatment. Results are based only on the information you share, which may be incomplete.
                    Please consult a licensed healthcare professional for medical advice or treatment.
                  </label>
                </div>
                <div className="d-flex justify-content-center align-items-center gap-3 mt-2 mb-2 auth-oauth-icon-row">
                  <AuthIconTooltip label="Continue With Google">
                    <GoogleIconSignIn
                      text="continue_with"
                      disabled={googleLoading || !isConsent}
                      onCredential={handleGoogleCredential}
                      onError={() => toast.error("Google sign-in was cancelled or failed.")}
                    />
                  </AuthIconTooltip>
                  <AuthIconTooltip label="Continue with Email">
                    <button
                      type="button"
                      className="btn btn-outline-light shadow-sm rounded-circle p-3 d-flex align-items-center justify-content-center icon-login-btn"
                      disabled={!isConsent}
                      onClick={() => {
                        if (!assertConsent()) return;
                        openEmailEntry();
                      }}
                    >
                      <Mail size={24} className="text-primary" />
                    </button>
                  </AuthIconTooltip>
                  <AuthIconTooltip label="Continue With Phone Number">
                    <button
                      type="button"
                      className="btn btn-outline-light shadow-sm rounded-circle p-3 d-flex align-items-center justify-content-center icon-login-btn"
                      disabled={!isConsent}
                      onClick={() => {
                        if (!assertConsent()) return;
                        openPhoneEntry();
                      }}
                    >
                      <Smartphone size={24} className="text-success" />
                    </button>
                  </AuthIconTooltip>
                </div>
                <div className="text-center px-40 pb-30 pt-2">
                  <p className="mb-0 text-muted">
                    Already have an account?{" "}
                    <Link to="/login" className="text-danger ms-1">
                      Sign In
                    </Link>
                  </p>
                </div>

                <ContactEntryDialogs
                  kind={contactEntryKind}
                  subtleBackdrop={guestChatSignup}
                  onClose={closeContactEntry}
                  draftFullName={draftFullName}
                  onDraftFullNameChange={setDraftFullName}
                  draftEmail={draftEmail}
                  onDraftEmailChange={setDraftEmail}
                  draftPhone={draftPhone}
                  onDraftPhoneChange={setDraftPhone}
                  fieldErrors={contactFieldErrors}
                  onClearContactFieldError={clearContactFieldError}
                  onEmailNext={submitEmailEntry}
                  onPhoneNext={submitPhoneEntry}
                  checkUser={setIsExistingUser}
                  isExistingUser={isExistingUser}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setOtpStep(false);
          setOtp(["", "", "", "", "", ""]);
          setHasOtpError(false);
        }}
        maxWidth="xs"
        fullWidth
        className="auth-modal glass-modal" // Use the new class

        BackdropProps={{
          style: {
            backgroundColor: guestChatSignup ? "transparent" : "rgba(0, 0, 0, 0.2)",
            backdropFilter: guestChatSignup ? "none" : "blur(5px)",
          },
        }}
      >
        <IconButton
          className="auth-modal-round-close"
          aria-label="close"
          onClick={() => {
            setShowModal(false);
            setOtpStep(false);
            setOtp(["", "", "", "", "", ""]);
            setHasOtpError(false);
          }}
        >
          <CloseIcon />
        </IconButton>
        {!otpStep ? (
          <>

          </>
        ) : (
          <>
            <DialogTitle className="text-center pt-30 pb-10">
              <h2>Enter Security Code</h2>
              <p className="text-muted fs-14 mb-0">
                Code sent to {verificationMethod === 'email' ? registerData?.email : `${country.code}${String(registerData?.phoneNumber ?? "").replace(/\D/g, "").slice(0, country.maxLength)}`}
              </p>
            </DialogTitle>
            <DialogContent className="px-40 pb-30">
              <div className="mt-3 text-center form-input-container">
                <div className="otp-container d-flex justify-content-between mb-30" style={{ gap: '10px' }}>
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      maxLength={1}
                      value={digit}
                      ref={(el) => { inputRefs.current[index] = el; }}
                      onChange={(e) => handleChangeOtp(e.target.value, index)}
                      onKeyDown={(e) => handleKeyDownOtp(e, index)}
                      className={`form-control otp-input ${digit ? "filled" : ""} ${hasOtpError ? "border-danger" : ""}`}
                      style={{ width: '45px', height: '55px', textAlign: 'center', fontSize: '24px', borderRadius: '10px', borderColor: hasOtpError ? '#dc3545' : undefined }}
                    />
                  ))}
                </div>
                <div className="auth-otp-resend">
                  <p className="auth-otp-resend__hint">Didn&apos;t receive the code?</p>
                  {countdown > 0 ? (
                    <div className="auth-otp-resend__timer" role="status" aria-live="polite" aria-atomic="true">
                      <span className="auth-otp-resend__timer-label">Resend in</span>
                      <span className="auth-otp-resend__timer-value">{countdown}s</span>
                    </div>
                  ) : (
                    <a
                      href="#"
                      className="auth-otp-resend__link"
                      onClick={(e) => {
                        e.preventDefault();
                        sendNewOtp();
                      }}
                    >
                      Resend code
                    </a>
                  )}
                </div>
              </div>
            </DialogContent>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default Register;
